import React, { useState, useEffect, useCallback } from 'react';
import { HiDocumentText, HiCurrencyRupee } from 'react-icons/hi2';
import { getInvoices } from '../../services/api';
import { format } from 'date-fns';

const statusBadge = s => {
  const map = { unpaid: 'badge-red', partial: 'badge-yellow', paid: 'badge-green', overdue: 'badge-red', cancelled: 'badge-gray' };
  return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
};

export default function CustomerBills() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await getInvoices(params);
      setInvoices(data.data);
      setTotal(data.total);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const totalDue = invoices
    .filter(i => ['unpaid', 'partial', 'overdue'].includes(i.status))
    .reduce((s, i) => s + (parseFloat(i.amount) - parseFloat(i.paid_amount)), 0);

  const totalPages = Math.ceil(total / 15);

  return (
    <div className="content-area">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Bills</h1>
          <p className="text-muted">{total} total invoices</p>
        </div>
      </div>

      {totalDue > 0 && (
        <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <HiCurrencyRupee />
          <span>You have <strong>Rs.{totalDue.toFixed(2)}</strong> outstanding balance. Please contact us to clear dues.</span>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ paddingBottom: '1rem' }}>
          <div className="filter-bar">
            <div>
              <label className="form-label">Filter by Status</label>
              <select
                className="form-control"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                style={{ width: 180 }}
              >
                <option value="">All Invoices</option>
                {['unpaid', 'partial', 'paid', 'overdue', 'cancelled'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
            <HiDocumentText style={{ fontSize: '3rem', display: 'block', margin: '0 auto 0.75rem' }} />
            No invoices found.
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Issued</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(i => {
                  const balance = parseFloat(i.amount) - parseFloat(i.paid_amount);
                  return (
                    <tr key={i.id}>
                      <td style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{i.invoice_number}</td>
                      <td>Rs.{parseFloat(i.amount).toFixed(2)}</td>
                      <td style={{ color: '#16a34a', fontWeight: 600 }}>Rs.{parseFloat(i.paid_amount).toFixed(2)}</td>
                      <td style={{ color: balance > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                        Rs.{balance.toFixed(2)}
                      </td>
                      <td>{statusBadge(i.status)}</td>
                      <td className="text-muted">{i.due_date ? format(new Date(i.due_date), 'dd MMM yyyy') : '-'}</td>
                      <td className="text-muted">{format(new Date(i.issued_at), 'dd MMM yyyy')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div style={{ padding: '1rem 1.5rem' }}>
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: '#f0f9ff', borderRadius: '0.75rem', border: '1px solid #bae6fd' }}>
        <p style={{ fontWeight: 600, color: '#0c4a6e', marginBottom: '0.4rem' }}>Need to make a payment?</p>
        <p style={{ color: '#0369a1', fontSize: '0.875rem' }}>
          Contact us at <strong>8374422411</strong> or pay via UPI to <strong>vijayavarshi@upi</strong>
        </p>
      </div>
    </div>
  );
}
